import GCarousel from "./g-carousel";
import { fetchSearchProducts } from "./g-utils";
import { CarouselManager } from "../utils/carouselManager";

class ProductsSameAuthor extends GCarousel {
  constructor() {
    super();
    this.dynamic = true;
    this._mountHandler = null; // Store handler reference for cleanup
    this._fetchItemsResolve = null; // Store promise resolve function
    this._modalObserver = null; // Store MutationObserver for cleanup
    this._refreshTimeout = null; // Store timeout for cleanup
  }

  async connectedCallback() {
    await this.initSlider();
  }

  disconnectedCallback() {
    // Clean up listener when component is removed from DOM
    if (this._mountHandler && this.splideEl) {
      this.splideEl.removeEventListener("carousel:start-mount", this._mountHandler);
      this._mountHandler = null;
    }
    // Resolve promise if pending to avoid leaks
    if (this._fetchItemsResolve) {
      this._fetchItemsResolve();
      this._fetchItemsResolve = null;
    }
    // Clean up MutationObserver
    if (this._modalObserver) {
      this._modalObserver.disconnect();
      this._modalObserver = null;
    }
    // Clean up timeout
    if (this._refreshTimeout) {
      clearTimeout(this._refreshTimeout);
      this._refreshTimeout = null;
    }
  }

  // Helper method to get splide instance (from this.splide or CarouselManager)
  getSplideInstance() {
    if (this.splide) {
      return this.splide;
    }
    // If this.splide is not defined, retrieve from CarouselManager
    const carouselId = this.splideEl?.dataset?.carouselId || this.splideEl?.id;
    if (carouselId && CarouselManager.instances.has(carouselId)) {
      return CarouselManager.instances.get(carouselId);
    }
    return null;
  }

  // Helper method to refresh slider and reset position
  _refreshSlider(splideInstance) {
    if (!splideInstance) return;
    
    splideInstance.refresh();
    if (splideInstance.index !== 0) {
      splideInstance.go(0);
    }
    this.splide = splideInstance;
  }

  // Helper method to wait for DOM update and refresh slider
  _refreshSliderAfterDOMUpdate(splideInstance) {
    if (!splideInstance) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._refreshSlider(splideInstance);
      });
    });
  }

  async fetchItems() {
    if (!this.isbn) {
      this.setAttribute("aria-hidden", true);
      return;
    }

    try {
      // Search products by same author
      const sameAuthorJson = await fetchSearchProducts({
        q: this.author,
        productsPerPage: this.recommendationsCount,
      });

      if (
        !sameAuthorJson?.results ||
        sameAuthorJson?.results.length === 0
      ) {
        this.setAttribute("aria-hidden", true);
        return;
      }

      const productIds = sameAuthorJson.results
        .filter((product) => product.id !== this.isbn)
        .map((product) => product.id);
      let sectionUrl = `?sections=ajax-products-same-author&product_ids=${productIds}`;
      if (this.dataset.carouselId) {
        sectionUrl += `&carousel_id=${this.dataset.carouselId}`;
      }
      if (this.dataset.carouselName) {
        sectionUrl += `&carousel_name=${encodeURI(this.dataset.carouselName)}`;
      }
      const sectionResponse = await fetch(window.Shopify.routes.root + sectionUrl);
      const sectionJson = await sectionResponse.json();
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sectionJson[Object.keys(sectionJson)[0]];
      const items = tempDiv.querySelector(".js-content");

      if (items && items.childElementCount > 0) {
        this.splideList.innerHTML = items.innerHTML;
        this.setAttribute("aria-hidden", false);
        
        // Use getSplideInstance() to ensure we have the instance
        const splideInstance = this.getSplideInstance();
        if (splideInstance) {
          this._handleSliderRefresh(splideInstance);
        }
      } else {
        this.setAttribute("aria-hidden", true);
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      this.setAttribute("aria-hidden", true);
      return;
    }
  }

  async initSlider() {
    let options = {};
    if (this.dataset.sliderOptions) {
      options = JSON.parse(this.dataset.sliderOptions);
    }

    // Clean up old handler if it exists (in case component is re-added)
    if (this._mountHandler && this.splideEl) {
      this.splideEl.removeEventListener("carousel:start-mount", this._mountHandler);
    }

    // Check if carousel is already mounted
    const carouselId = this.splideEl?.dataset?.carouselId || this.splideEl?.id;
    const isCarouselAlreadyMounted = carouselId && CarouselManager.instances.has(carouselId);

    // If carousel already exists, destroy it to force complete reinitialization
    // This is necessary because modal content changes on each opening
    if (isCarouselAlreadyMounted) {
      CarouselManager.destroy(carouselId);
      this.splide = null;
    }

    const fetchItemsPromise = new Promise((resolve) => {
      this._fetchItemsResolve = resolve;
      
      if (this.dataset.lazyloadSection === "true" || this.dynamic) {
        this._mountHandler = async () => {
          await this.fetchItems();
          // fetchItems() already handles refresh with requestAnimationFrame
          // Don't remove listener here to allow re-executions
          // Listener will be cleaned up in disconnectedCallback
          if (this._fetchItemsResolve) {
            this._fetchItemsResolve();
            this._fetchItemsResolve = null;
          }
        };

        // Wait for "carousel:start-mount" event
        // Even if carousel was mounted before, we destroyed it, so we wait for the event
        this.splideEl.addEventListener("carousel:start-mount", this._mountHandler);
      } else {
        resolve();
        this._fetchItemsResolve = null;
      }
    });

    // Let CarouselManager handle initialization and observation
    const [_, carouselInstance] = await Promise.all([
      fetchItemsPromise,
      CarouselManager.create(this.splideEl, options),
    ]);
    this.splide = carouselInstance;
    
    // If carousel is mounted immediately (no lazy loading), refresh it
    if (this.splide) {
      this.splide.refresh();
    }

    this.initWheel();
    if (this.dataset.arrows === "true") {
      this.initSliderArrows();
    }
    if (this.dataset.progressBar) {
      this.initSliderProgressBar();
    }
    this.classList.add("js-loaded");
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get isbn() {
    return this.dataset.isbn;
  }

  get author() {
    return this.dataset.author;
  }

  get recommendationsCount() {
    return Number(this.dataset.recommendationsCount);
  }

  // Handle slider refresh with modal visibility check
  _handleSliderRefresh(splideInstance) {
    if (!splideInstance) return;

    // Check if component is in a modal and if it's visible
    const modal = this.closest('g-modal, fetch-modal, dynamic-modal');
    const isModalVisible = !modal || modal.expanded;
    
    if (isModalVisible) {
      // Modal is visible, refresh immediately
      this._refreshSliderAfterDOMUpdate(splideInstance);
    } else {
      // Modal is not yet visible, wait for it to be visible
      this._waitForModalAndRefresh(modal, splideInstance);
    }
  }

  // Wait for modal to become visible and then refresh slider
  _waitForModalAndRefresh(modal, splideInstance) {
    if (!modal) {
      // No modal found, refresh anyway
      this._refreshSliderAfterDOMUpdate(splideInstance);
      return;
    }

    // Clean up previous observer if exists
    if (this._modalObserver) {
      this._modalObserver.disconnect();
    }

    // Observe modal aria-expanded attribute changes
    this._modalObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
          if (modal.expanded) {
            // Modal is now visible, refresh the slider
            this._refreshSliderAfterDOMUpdate(splideInstance);
            this._modalObserver.disconnect();
            this._modalObserver = null;
            
            // Clear timeout if it exists
            if (this._refreshTimeout) {
              clearTimeout(this._refreshTimeout);
              this._refreshTimeout = null;
            }
            return;
          }
        }
      }
    });
    
    this._modalObserver.observe(modal, { attributes: true });
    
    // Safety timeout in case modal doesn't open
    if (this._refreshTimeout) {
      clearTimeout(this._refreshTimeout);
    }
    
    this._refreshTimeout = setTimeout(() => {
      this._refreshSliderAfterDOMUpdate(splideInstance);
      if (this._modalObserver) {
        this._modalObserver.disconnect();
        this._modalObserver = null;
      }
      this._refreshTimeout = null;
    }, 1000);
  }
}

if (!customElements.get("products-same-author")) {
  customElements.define("products-same-author", ProductsSameAuthor);
}
